import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import {
  actualizarBiblioteca,
  estadoActualizacion,
  obtenerBiblioteca,
  ultimaActualizacion,
  type Manga,
} from '../api/consultas'
import Finalizar from '../componentes/Finalizar'
import Hoja from '../componentes/Hoja'
import Portada from '../componentes/Portada'
import Novedades, { hayNovedadesSinLeer, useNovedades } from '../componentes/Novedades'
import Zangetsu from '../componentes/Zangetsu'
import { leerFinalizada } from '../lib/finalizadas'
import { IA_ACTIVA } from '../lib/ia'
import { useMantenerPulsado } from '../lib/pulsacion'
import { aplicarTamano, leerTamano, TAMANOS, type Tamano } from '../lib/rejilla'

function haceCuanto(milisegundos: number): string {
  if (!milisegundos) return 'nunca'
  const minutos = Math.round((Date.now() - milisegundos) / 60000)
  if (minutos < 2) return 'ahora mismo'
  if (minutos < 60) return `hace ${minutos} min`
  const horas = Math.round(minutos / 60)
  if (horas < 24) return `hace ${horas} h`
  const dias = Math.round(horas / 24)
  return `hace ${dias} ${dias === 1 ? 'dia' : 'dias'}`
}

/** Una portada de la rejilla. Aparte del resto porque cada una necesita su
 *  propio gesto de mantener pulsado, y los hooks no van dentro de un map. */
function Tarjeta({ manga, alPulsar }: { manga: Manga; alPulsar: () => void }) {
  const pulsar = useMantenerPulsado(alPulsar)
  return (
    <Link to={`/manga/${manga.id}`} className="tarjeta" {...pulsar}>
      <Portada
        mangaId={manga.id}
        ruta={manga.thumbnailUrl}
        titulo={manga.title}
        sinLeer={manga.unreadCount}
      />
      <div className="titulo-tarjeta">{manga.title}</div>
    </Link>
  )
}

export default function Biblioteca() {
  const clienteConsultas = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['biblioteca'],
    queryFn: obtenerBiblioteca,
  })

  // Mientras el servidor comprueba fuentes se sondea el progreso; en reposo
  // no se pregunta nada.
  const { data: estado } = useQuery({
    queryKey: ['estadoActualizacion'],
    queryFn: estadoActualizacion,
    refetchInterval: (consulta) => (consulta.state.data?.enMarcha ? 1500 : false),
  })

  const { data: ultima } = useQuery({
    queryKey: ['ultimaActualizacion'],
    queryFn: ultimaActualizacion,
  })

  const actualizar = useMutation({
    mutationFn: actualizarBiblioteca,
    onSuccess: () => clienteConsultas.invalidateQueries({ queryKey: ['estadoActualizacion'] }),
  })

  // Al terminar hay que recargar la biblioteca para ver los capitulos nuevos
  const estabaEnMarcha = useRef(false)
  useEffect(() => {
    if (estabaEnMarcha.current && !estado?.enMarcha) {
      void clienteConsultas.invalidateQueries({ queryKey: ['biblioteca'] })
      void clienteConsultas.invalidateQueries({ queryKey: ['ultimaActualizacion'] })
    }
    estabaEnMarcha.current = Boolean(estado?.enMarcha)
  }, [estado?.enMarcha, clienteConsultas])

  const enMarcha = estado?.enMarcha ?? false

  const [tamano, setTamano] = useState<Tamano>(leerTamano)
  const [eligiendoTamano, setEligiendoTamano] = useState(false)
  const [preguntando, setPreguntando] = useState(false)
  const [viendoNovedades, setViendoNovedades] = useState(false)
  /** La serie a la que se le mantuvo pulsada la portada, si hay alguna. */
  const [marcando, setMarcando] = useState<Manga | null>(null)

  /*
   * Las finalizadas no se pintan aqui: estan en su estante. Se quedan en la
   * biblioteca de Suwayomi a proposito —sacarlas se llevaria por delante el
   * progreso y las copias—, asi que el filtro es de esta pantalla y de nadie
   * mas.
   */
  const series = useMemo(() => (data ?? []).filter((m) => !leerFinalizada(m.meta)), [data])
  const finalizadas = (data?.length ?? 0) - series.length

  // Se pide aqui, y no solo al abrir la hoja, para poder marcar el boton
  // cuando hay algo nuevo sin leer. React Query comparte la respuesta con la
  // hoja, asi que no son dos peticiones.
  const { data: novedades } = useNovedades()

  function cambiarTamano(nuevo: Tamano) {
    aplicarTamano(nuevo)
    setTamano(nuevo)
  }

  return (
    <>
      <div className="cabecera-lista">
        <h1>Biblioteca</h1>
        {IA_ACTIVA && (
          <button
            className="icono-accion zangetsu-boton"
            title="Preguntar a Zangetsu"
            onClick={() => setPreguntando(true)}
          >
            斬
          </button>
        )}
        <button
          className="icono-accion boton-novedades"
          title="Capitulos nuevos de tus series"
          onClick={() => setViendoNovedades(true)}
        >
          🔔
          {hayNovedadesSinLeer(novedades) && <span className="punto-nuevo" />}
        </button>
        <Link className="icono-accion" to="/finalizadas" title="Series finalizadas">
          ✓
        </Link>
        <button
          className="icono-accion"
          title="Tamano de las portadas"
          onClick={() => setEligiendoTamano(true)}
        >
          ▦
        </button>
        <button
          className="icono-accion"
          title="Buscar capitulos nuevos"
          onClick={() => actualizar.mutate()}
          disabled={enMarcha || actualizar.isPending}
        >
          <span className={enMarcha ? 'girando' : undefined}>↻</span>
        </button>
      </div>

      {IA_ACTIVA && preguntando && <Zangetsu alCerrar={() => setPreguntando(false)} />}

      {viendoNovedades && <Novedades alCerrar={() => setViendoNovedades(false)} />}

      {eligiendoTamano && (
        <Hoja
          titulo="Tamano de las portadas"
          nota="Se aplica tambien a Buscar y Explorar."
          alCerrar={() => setEligiendoTamano(false)}
        >
          {(cerrar) =>
            TAMANOS.map((opcion) => (
              <button
                key={opcion.id}
                className={opcion.id === tamano ? 'boton' : 'boton secundario'}
                onClick={() => {
                  // El tamano se aplica ya, y la rejilla cambia mientras la
                  // hoja baja
                  cambiarTamano(opcion.id)
                  cerrar()
                }}
              >
                {opcion.nombre}
                <small>{opcion.nota}</small>
              </button>
            ))
          }
        </Hoja>
      )}

      <p className="subtitulo">
        {enMarcha
          ? `Buscando capitulos nuevos… ${estado!.hechos} de ${estado!.total}`
          : series.length
            ? `${series.length} series · comprobado ${haceCuanto(ultima ?? 0)}` +
              (finalizadas ? ` · ${finalizadas} finalizadas` : '')
            : 'Tus series guardadas'}
      </p>

      {enMarcha && estado!.total > 0 && (
        <div className="progreso">
          <div style={{ width: `${Math.round((estado!.hechos / estado!.total) * 100)}%` }} />
        </div>
      )}

      {isLoading && <p className="cargando">Cargando…</p>}

      {error && (
        <div className="aviso error">
          <strong>No se pudo cargar</strong>
          <p>{(error as Error).message}</p>
        </div>
      )}

      {data?.length === 0 && (
        <div className="aviso">
          <strong>Todavia no hay nada aqui</strong>
          <p>
            Ve a <Link to="/explorar">Explorar</Link> o <Link to="/buscar">Buscar</Link> y anade
            series. Apareceran en esta pantalla, y el servidor comprobara sus capitulos nuevos cada
            6 horas.
          </p>
        </div>
      )}

      {data && data.length > 0 && series.length === 0 && (
        <div className="aviso">
          <strong>Todo finalizado</strong>
          <p>
            Las {finalizadas} series que tienes estan en <Link to="/finalizadas">Finalizadas</Link>.
            Busca algo nuevo en <Link to="/explorar">Explorar</Link>.
          </p>
        </div>
      )}

      {series.length > 0 && (
        <div className="rejilla">
          {series.map((manga) => (
            <Tarjeta key={manga.id} manga={manga} alPulsar={() => setMarcando(manga)} />
          ))}
        </div>
      )}

      {marcando && (
        <Finalizar
          mangaId={marcando.id}
          titulo={marcando.title}
          finalizada={leerFinalizada(marcando.meta)}
          alCerrar={() => setMarcando(null)}
        />
      )}
    </>
  )
}
